/**
 * Session Timeout Provider
 * 
 * Wraps the application to provide session timeout functionality.
 * Shows a warning dialog before automatic logout.
 */

import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useSessionTimeout, TimeoutDuration, TIMEOUT_DURATIONS } from "@/hooks/useSessionTimeout";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Clock, Shield, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";

interface SessionTimeoutContextType {
  timeout: TimeoutDuration;
  setTimeout: (duration: TimeoutDuration) => void;
  isEnabled: boolean;
  setEnabled: (enabled: boolean) => void;
  extendSession: () => void;
}

const SessionTimeoutContext = createContext<SessionTimeoutContextType | undefined>(undefined);

export function useSessionTimeoutContext() {
  const context = useContext(SessionTimeoutContext);
  if (!context) {
    throw new Error("useSessionTimeoutContext must be used within SessionTimeoutProvider");
  }
  return context;
}

interface SessionTimeoutProviderProps {
  children: ReactNode;
  defaultTimeout?: TimeoutDuration;
  defaultEnabled?: boolean;
}

export function SessionTimeoutProvider({
  children,
  defaultTimeout = "4h",
  defaultEnabled = true,
}: SessionTimeoutProviderProps) {
  const { user } = useAuth();
  
  // Load settings from localStorage
  const [timeout, setTimeoutState] = useState<TimeoutDuration>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("session_timeout");
      if (stored && stored in TIMEOUT_DURATIONS) {
        return stored as TimeoutDuration;
      }
    }
    return defaultTimeout;
  });
  
  const [isEnabled, setEnabledState] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("session_timeout_enabled");
      return stored === null ? defaultEnabled : stored === "true";
    }
    return defaultEnabled;
  });
  
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  
  // Persist settings
  const setTimeout = (duration: TimeoutDuration) => {
    setTimeoutState(duration);
    localStorage.setItem("session_timeout", duration);
  };
  
  const setEnabled = (enabled: boolean) => {
    setEnabledState(enabled);
    localStorage.setItem("session_timeout_enabled", String(enabled));
  };
  
  const {
    isWarningShown,
    remainingSeconds,
    extendSession,
  } = useSessionTimeout({
    timeout,
    enabled: isEnabled && !!user,
    onWarning: () => setShowWarningDialog(true),
  });
  
  // Sync warning dialog with hook state
  useEffect(() => {
    if (!isWarningShown) {
      setShowWarningDialog(false);
    }
  }, [isWarningShown]);
  
  const handleExtendSession = () => {
    extendSession();
    setShowWarningDialog(false);
  };
  
  return (
    <SessionTimeoutContext.Provider
      value={{
        timeout,
        setTimeout,
        isEnabled,
        setEnabled,
        extendSession,
      }}
    >
      {children}
      
      {/* Session Expiry Warning Dialog */}
      <AlertDialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-warning" />
              Session Expiring
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Your session will expire in{" "}
                <span className="font-bold text-warning">{remainingSeconds} seconds</span>{" "}
                due to inactivity.
              </p>
              <div className="flex items-center justify-center py-4">
                <motion.div
                  className="relative h-24 w-24"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: [0.9, 1, 0.9] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <div className="absolute inset-0 rounded-full border-4 border-muted" />
                  <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="46"
                      fill="none"
                      stroke="hsl(var(--warning))"
                      strokeWidth="8"
                      strokeDasharray={`${(remainingSeconds / 60) * 289} 289`}
                      strokeLinecap="round"
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <Clock className="h-6 w-6 text-warning mb-1" />
                    <span className="text-2xl font-bold">{remainingSeconds}</span>
                  </div>
                </motion.div>
              </div>
              <p className="text-sm text-center">
                Click the button below to continue your session.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction asChild>
              <Button onClick={handleExtendSession} className="w-full gap-2">
                <RefreshCw className="h-4 w-4" />
                Continue Session
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SessionTimeoutContext.Provider>
  );
}

export default SessionTimeoutProvider;
